import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Modal } from "../../common-components/modal";
import ShortsWrapper from "./shorts-wrapper";
import ShortsList from "./shorts-list";
import ShortsPreset from "./shorts-preset";
import ShortsDislikeModal from "./shorts-dislike-modal";
import ShortsListBanner from "./ShortsListBanner";
import { PLAN } from "../../constants";
import { API, API_URL } from "../../constants/config";
import useApi from "../../helper/hooks/useApi";
import useNotify from "../../helper/hooks/useNotify";
import { useShortsContext } from "./shortsContextApi";
import contextActionType from "./context-action-type";
import { setBackdropLoader } from "../../redux/actions/appUtils";
import BackdropLoader from "../../common-components/BackdropLoader";
import openUrl from "../../helper/openUrl";
import useUpgrade from "../../helper/hooks/useUpgrade";
import TRACKERS from "../../constants/trackers";
import trackerEvents from "../../helper/trackerEvents";


const { CLOSE_SHORT_PRESET, GET_PRESET_MODAL_OPEN, GET_PLAYER_REMOUNT_KEY } = contextActionType;

const FEEDBACK_TYPE = {
  LIKE: "like",
  UNDO_LIKE: "undo_like",
  DISLIKE: "dislike",
  UNDO_DISLIKE: "undo_dislike",
};

const isActiveFeedbackType = (type) =>
  type === FEEDBACK_TYPE.LIKE || type === FEEDBACK_TYPE.DISLIKE;

const isUndoFeedbackType = (type) =>
  type === FEEDBACK_TYPE.UNDO_LIKE || type === FEEDBACK_TYPE.UNDO_DISLIKE;

const getFeedbackStorageKey = (projectId) => `vm-shorts-feedback-${projectId}`;

const readFeedbackCache = (projectId) => {
  if (!projectId) return { map: {}, hasCache: false };
  try {
    const raw = sessionStorage.getItem(getFeedbackStorageKey(projectId));
    if (raw) {
      return { map: JSON.parse(raw), hasCache: true };
    }
  } catch {
    // ignore invalid cache
  }
  return { map: {}, hasCache: false };
};

const writeFeedbackCache = (projectId, map) => {
  if (!projectId) return;
  try {
    sessionStorage.setItem(getFeedbackStorageKey(projectId), JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
};

const ShortsListModal = ({ shorts, projectDetails, userDetails, params, setBackdropLoader, backdropLoader }) => {
  const { plan, projectId } = userDetails;
  const initialFeedbackCache = useMemo(() => readFeedbackCache(projectId), [projectId]);
  const [activeShort, setActiveShort] = useState(null);
  const activeShortId = activeShort?.get ? activeShort.get("id") : activeShort?.id;
  const [modalScrollKey, setModalScrollKey] = useState(0);
  const [viewedShortIds, setViewedShortIds] = useState(new Set());
  const [feedbackMap, setFeedbackMap] = useState(initialFeedbackCache.map);
  const [isFeedbackLoaded, setIsFeedbackLoaded] = useState(initialFeedbackCache.hasCache);
  const [dislikeShortId, setDislikeShortId] = useState(null);
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const { getShortsContext, setShortsContext } = useShortsContext();
  const presetModalOpen = getShortsContext(GET_PRESET_MODAL_OPEN);
  const playerRemountKey = getShortsContext(GET_PLAYER_REMOUNT_KEY);
  const { post } = useApi();
  const { success } = useNotify();
  const { showPricingModal } = useUpgrade();

  useEffect(() => {
    if (activeShortId && shorts) {
      const updatedShort = shorts.get ? shorts.get(activeShortId) : shorts[activeShortId];
      if (updatedShort && updatedShort !== activeShort) {
        setActiveShort(updatedShort.set ? updatedShort.set("id", activeShortId) : { ...updatedShort, id: activeShortId });
      }
    }
  }, [shorts, activeShortId, activeShort]);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;

    post(`${API_URL}${API.SHORTS.FEEDBACK_PROJECT}`, { projectId })
      .then((res) => {
        if (cancelled) return;
        if (res.error || !res.shorts) return;
        const map = {};
        res.shorts.forEach((item) => {
          if (item.shortId) {
            if (!isActiveFeedbackType(item.feedbackType)) return;
            map[item.shortId] = {
              feedbackType: item.feedbackType,
              feedbackText: item.feedbackText || "",
            };
          }
        });
        setFeedbackMap(map);
        writeFeedbackCache(projectId, map);
      })
      .finally(() => {
        if (!cancelled) {
          setIsFeedbackLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- post from useApi is not stable across renders
  }, [projectId]);

  const saveFeedback = useCallback(
    (shortId, feedbackType, feedbackText = "") => {
      if (!projectId || !shortId) return Promise.resolve();
      setIsSavingFeedback(true);
      const payload = { projectId, shortId, feedbackType, feedbackText };
      return post(`${API_URL}${API.SHORTS.FEEDBACK_SAVE}`, payload)
        .then((res) => {
          if (!res.error) {
            if (isUndoFeedbackType(feedbackType)) {
              setFeedbackMap((prev) => {
                const next = { ...prev };
                delete next[shortId];
                writeFeedbackCache(projectId, next);
                return next;
              });
            } else {
              setFeedbackMap((prev) => {
                const next = {
                  ...prev,
                  [shortId]: { feedbackType, feedbackText },
                };
                writeFeedbackCache(projectId, next);
                return next;
              });
            }
          }
          return res;
        })
        .finally(() => {
          setIsSavingFeedback(false);
        });
    },
    [post, projectId],
  );

  const handleLike = useCallback(
    (shortId) => {
      const current = feedbackMap[shortId]?.feedbackType;
      if (current === FEEDBACK_TYPE.LIKE) {
        saveFeedback(shortId, FEEDBACK_TYPE.UNDO_LIKE);
        return;
      }
      saveFeedback(shortId, FEEDBACK_TYPE.LIKE).then((res) => {
        if (!res?.error) {
          success("Thanks for the feedback");
        }
      });
    },
    [feedbackMap, saveFeedback, success],
  );

  const handleDislikeClick = useCallback((shortId) => {
    if (feedbackMap[shortId]?.feedbackType === FEEDBACK_TYPE.DISLIKE) {
      const { feedbackText = "" } = feedbackMap[shortId] || {};
      saveFeedback(shortId, FEEDBACK_TYPE.UNDO_DISLIKE, feedbackText);
      return;
    }
    setDislikeShortId(shortId);
    setShortsContext(CLOSE_SHORT_PRESET);
  }, [feedbackMap, saveFeedback, setShortsContext]);

  const handleDislikeClose = useCallback(() => {
    setDislikeShortId(null);
  }, []);

  const handleDislikeSubmit = useCallback(
    (feedbackText) => {
      if (!dislikeShortId) return;
      const submittedShortId = dislikeShortId;
      saveFeedback(submittedShortId, FEEDBACK_TYPE.DISLIKE, feedbackText).then((res) => {
        if (!res.error) {
          trackerEvents(TRACKERS.LFSF_VIDEO_DISLIKED, {
            projectId,
            shortId: submittedShortId,
            feedbackText,
            isBoth: 1,
          });
          success("Thanks for the feedback");
          setDislikeShortId(null);
          setActiveShort(null);
        }
      });
    },
    [dislikeShortId, projectId, saveFeedback, success],
  );

  const handleSelectShort = useCallback(
    (short) => {
      const shortId = short.get ? short.get("id") : short.id;
      setViewedShortIds((prev) => new Set([...prev, shortId]));
      setActiveShort(short.set ? short.set("id", shortId) : { ...short, id: shortId });
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setActiveShort(null);
    setModalScrollKey((key) => key + 1);
    setShortsContext(CLOSE_SHORT_PRESET);
  }, [setShortsContext]);

  const handleClosePreset = useCallback(() => {
    setShortsContext(CLOSE_SHORT_PRESET);
  }, [setShortsContext]);

  const { sourceType } = userDetails;
  const isDummyProject = sourceType === "shorts-dummy";
  const hasNoAudio = sourceType === "Shorts-No-Audio";
  const hasNoTranscription = sourceType === "Short-No-Transcription";

  useEffect(() => {
    if (sourceType === "shorts-dummy" && plan !== PLAN.LITE) {
      setBackdropLoader({
        isLoading: true,
        loaderMessage: "Generating the project...",
      });
      post(`${API_URL}${API.SHORTS.DUMMY_REGENERATE}`, { projectId }).then((res) => {
        openUrl(`${API_URL}dashboard/long-to-short/upload/${res.docId}/${res.taskId}`, true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType, plan, projectId])

  // Wait for shorts data so banner doesn't flash the wrong type before load.
  const isShortsDataReady = shorts !== undefined && shorts !== null;

  const handleUpgradeClick = useCallback(() => {
    showPricingModal({ type: "longtoshort", source: "longtoshort" });
    trackerEvents(TRACKERS.LFTS_DUMMY_PROJECT_UPGRADE, {
      projectId: userDetails.projectId,
      source: "shorts-list-modal-upgrade-btn",
      isBoth: 1,
    });
  }, [showPricingModal, userDetails.projectId]);

  return (
    <>
      <ShortsListBanner
        isDummyProject={isDummyProject}
        hasNoAudio={hasNoAudio}
        hasNoTranscription={hasNoTranscription}
        onUpgradeClick={handleUpgradeClick}
        sourceType={sourceType}
        isReady={isShortsDataReady}
      />
      <ShortsList
        shorts={shorts}
        projectDetails={projectDetails}
        onSelectShort={handleSelectShort}
        viewedShortIds={viewedShortIds}
        feedbackMap={feedbackMap}
        isFeedbackLoaded={isFeedbackLoaded}
        onLike={handleLike}
        onDislike={handleDislikeClick}
      />

      <Modal
        key={`shorts-player-modal-${modalScrollKey}`}
        showModal={!!activeShort && !dislikeShortId}
        onClose={handleCloseModal}
        showBackdrop
        width="auto"
        height="auto"
        maxHeight="min(630px, calc(100dvh - 32px))"
        smHeight="auto"
        overflow="auto"
        smOverflow="auto"
        mdOverflow="auto"
        closeIconMobRight={"8px"}
        borderRadius="12px"
        closeIconPath={'vm-close-new.svg'}
        closeIconWidth="24px"
        closeIconHeight="24px"
        closeIconRight="12px"
        closeIconTop="12px"
        closeIconMobTop="1%"
        isDisabled
      >
        {activeShort && (
          <ShortsWrapper
            key={playerRemountKey}
            inModal
            params={params}
            projectDetails={projectDetails}
            userDetails={userDetails}
            shortData={activeShort}
            feedbackType={feedbackMap[activeShortId]?.feedbackType || null}
            onLike={() => handleLike(activeShortId)}
            onDislike={() => handleDislikeClick(activeShortId)}
          />
        )}
      </Modal>
      <ShortsPreset
        isShowModal={presetModalOpen}
        onClose={handleClosePreset}
        showBackdrop
      />
      <ShortsDislikeModal
        showModal={!!dislikeShortId}
        onClose={handleDislikeClose}
        onSubmit={handleDislikeSubmit}
        isSubmitting={isSavingFeedback}
      />
      {backdropLoader.get("isLoading") && (
        <BackdropLoader
          content={backdropLoader.get("loaderMessage")}
        />
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    setBackdropLoader: bindActionCreators(setBackdropLoader, dispatch)
  };
};

const mapStateToProps = ({ app }) => {
  return {
    backdropLoader: app.get("backdropLoader")
  };
};

ShortsListModal.propTypes = {
  shorts: PropTypes.object,
  projectDetails: PropTypes.object,
  userDetails: PropTypes.object,
  params: PropTypes.object,
  setBackdropLoader: PropTypes.func,
  backdropLoader: PropTypes.bool
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(ShortsListModal));